import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { publicQuoteService } from '../services/api';
import { FileText, Loader2, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';

const STATUS_META = {
  sent: { label: 'Sent', icon: FileText, color: 'text-blue-700', bg: 'bg-blue-50', ring: 'ring-blue-200' },
  viewed: { label: 'Viewed', icon: Clock, color: 'text-indigo-700', bg: 'bg-indigo-50', ring: 'ring-indigo-200' },
  accepted: { label: 'Accepted', icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  declined: { label: 'Declined', icon: XCircle, color: 'text-rose-700', bg: 'bg-rose-50', ring: 'ring-rose-200' },
  expired: { label: 'Expired', icon: AlertCircle, color: 'text-gray-600', bg: 'bg-gray-50', ring: 'ring-gray-200' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-50', ring: 'ring-gray-200' },
};

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString() : '—'; } catch { return d || '—'; } };

const PublicQuotePage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const data = await publicQuoteService.getByToken(token);
        if (active) setQuote(data);
      } catch (e) {
        if (!active) return;
        const status = e?.response?.status;
        if (status === 404) setError('This quote could not be found. Please check your link.');
        else if (status === 403) setError('This quote portal is not currently available.');
        else setError('We could not load this quote. Please try again later.');
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [token]);

  const meta = quote ? (STATUS_META[quote.status] || STATUS_META.sent) : null;
  const StatusIcon = meta?.icon || FileText;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50" data-testid="public-quote-page">
      <PublicNavbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-10 sm:py-14">
        {loading ? (
          <div className="flex justify-center py-24" data-testid="public-quote-loading">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center" data-testid="public-quote-error">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900">Quote unavailable</h1>
            <p className="text-gray-600 mt-2">{error}</p>
            <Link to="/" className="inline-block mt-6 text-blue-600 hover:underline" data-testid="public-quote-home-link">Return to home</Link>
          </div>
        ) : quote ? (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-testid="public-quote-card">
            <div className="px-6 sm:px-8 py-6 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-400 font-medium">Quote</p>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="public-quote-number">#{quote.quote_number}</h1>
                <p className="text-sm text-gray-500 mt-1 capitalize">{(quote.service_type || 'general').replace('_', ' ')} service</p>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ring-1 ${meta.bg} ${meta.color} ${meta.ring}`} data-testid="public-quote-status">
                <StatusIcon className="w-4 h-4" /> {meta.label}
              </span>
            </div>

            <div className="px-6 sm:px-8 py-6">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="public-quote-items">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100">
                      <th className="py-2 pr-4">Description</th>
                      <th className="py-2 px-4 text-right">Qty</th>
                      <th className="py-2 px-4 text-right">Unit</th>
                      <th className="py-2 pl-4 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(quote.line_items || []).map((li, i) => (
                      <tr key={i} data-testid={`public-quote-item-${i}`}>
                        <td className="py-3 pr-4 text-gray-800">{li.description}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{li.quantity}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{money(li.unit_price)}</td>
                        <td className="py-3 pl-4 text-right font-medium text-gray-900">{money(li.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 ml-auto max-w-xs space-y-2 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span data-testid="public-quote-subtotal">{money(quote.subtotal)}</span></div>
                <div className="flex justify-between text-gray-600"><span>Tax ({((quote.tax_rate || 0) * 100).toFixed(2)}%)</span><span data-testid="public-quote-tax">{money(quote.tax)}</span></div>
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-100"><span>Total</span><span data-testid="public-quote-total">{money(quote.total)}</span></div>
              </div>

              {quote.notes && (
                <div className="mt-6 bg-gray-50 border border-gray-100 rounded-lg p-4 text-sm text-gray-700" data-testid="public-quote-notes">
                  <p className="font-medium text-gray-900 mb-1">Notes</p>
                  {quote.notes}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm text-gray-500">
                {quote.valid_until && <p data-testid="public-quote-valid-until">Valid until <span className="text-gray-800 font-medium">{fmtDate(quote.valid_until)}</span></p>}
                <p>Issued <span className="text-gray-800 font-medium">{fmtDate(quote.sent_at || quote.created_at)}</span></p>
              </div>

              <p className="mt-8 text-xs text-gray-400 text-center">
                Questions about this quote? Reply to the message you received or contact our team. No income or amount is guaranteed until a service agreement is executed.
              </p>
            </div>
          </div>
        ) : null}
      </main>
      <PublicFooter />
    </div>
  );
};

export default PublicQuotePage;
